import { useState } from 'react';
import useFormValidation from '../../../../../hooks/useFormValidation';
import useAnotherDataFieldsConfig from './useAnotherDataFieldsConfig';
import { useTranslation } from 'react-i18next';
import { HAS_SMOKING_PLACES_OPTIONS, IS_SMOKING_ALLOWED_OPTIONS } from '../consts';
import useAlerts from './useAlerts';

export interface AnotherDataFormData {
    hasSmokinPlaces: string | null;
    isSmokingAllowed: string | null;
    observations: string | null;
}

const INITIAL_VALUES = {
    hasSmokinPlaces: '',
    isSmokingAllowed: '',
    observations: ''
};

const getOptionByValue = (value: string, options: any[]) => {
    return options.find((option) => option.value === value);
};

const getInitialFormValues = (initialEstablishmentData: any) => {
    if (!initialEstablishmentData) return INITIAL_VALUES;
    return {
        hasSmokinPlaces:
            (initialEstablishmentData?.permiteFumarLugarCerrado &&
                getOptionByValue(
                    initialEstablishmentData.permiteFumarLugarCerrado,
                    HAS_SMOKING_PLACES_OPTIONS
                )) ||
            '',
        isSmokingAllowed:
            (initialEstablishmentData?.permiteFumar &&
                getOptionByValue(
                    initialEstablishmentData.permiteFumar,
                    IS_SMOKING_ALLOWED_OPTIONS
                )) ||
            '',
        observations: initialEstablishmentData?.observacion || ''
    };
};

const useAnotherDataForm = (initialEstablishmentData: any) => {
    const initialData = getInitialFormValues(initialEstablishmentData?.establecimiento);
    const [initialFormValues] = useState(initialData);
    const [formData, setFormData] = useState(initialData);

    const { t } = useTranslation('carcinogenicReport', { keyPrefix: 'modules.establishmentData' });
    const {
        errors,
        hasValidationErrors,
        validateForm,
        handleBlurValidation,
        resetValidation,
        setErrors
    } = useFormValidation();
    
    const {showMissingRequiredFieldsAlert} = useAlerts({ translate: t });

    const { anotherDataFieldsConfig } = useAnotherDataFieldsConfig({
        t,
        anotherDataFormData: formData,
        handleBlurValidation
    });

    const handleChange = (name: string, value: any) => {
        setErrors((prevState) => ({
            ...prevState,
            [name]: { hasError: false, errorMessage: '' }
        }));
        setFormData((prevState) => ({
            ...prevState,
            [name]: value
        }));
    };

    const handleSave = async (e: any) => {
        e.preventDefault();
        if (!validateForm(formData, anotherDataFieldsConfig)){
            showMissingRequiredFieldsAlert();
            return;
        } 
    };

    const hasFormDataChanged =
        formData.hasSmokinPlaces !== initialFormValues.hasSmokinPlaces ||
        formData.isSmokingAllowed !== initialFormValues.isSmokingAllowed ||
        formData.observations !== initialFormValues.observations;

    return {
        formData,
        errors,
        anotherDataFieldsConfig,
        hasFormDataChanged,
        handleChange,
        handleSave,
        validateForm
    };
};

export default useAnotherDataForm;
